import React, { useState, useEffect } from 'react';
import { Input, InputNumber, Select } from 'antd';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';
import useXmlPattern from '../../../utils/xmlPattern';

const useFields = (t, form, id) => {
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [postalCode, setPostalCode] = useState();
  const [cities, setCities] = useState();
  const [insurer, setInsurer] = useState();
  const [enums, setEnums] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { Option } = Select;

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/insurers/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getInsurer = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insurers/${id}`
      });
      setInsurer(data);
      form.setFieldsValue({ ...data });
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  // eslint-disable-next-line consistent-return
  const getCity = async (postal_code) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/${postal_code}`
      });

      const tmpData = data
        .map((city) => {
          if (city.codesPostaux.length > 1) {
            return city.codesPostaux.map((codePostal) => ({
              nom: city.nom.toUpperCase(),
              codePostal
            }));
          }
          return {
            nom: city.nom.toUpperCase(),
            codePostal: city.codesPostaux[0]
          };
        })
        .flat();

      setCities(tmpData);
      return tmpData;
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const handlePostalCodeChange = async (event) => {
    const { value } = event.target;
    if (value && value.length >= 2) {
      setPostalCode(value);
      const tmpCities = await getCity(value.slice(0, 2));

      if (tmpCities) {
        const filteredCities = tmpCities.filter((city) => {
          let isMatching = false;

          if (city.codePostal.startsWith(value)) {
            isMatching = true;
          }

          return isMatching;
        });

        if (filteredCities.length === 1) {
          form.setFieldsValue({
            complaint_city: `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
          });
        }
      } else {
        form.setFieldsValue({
          complaint_city: undefined
        });
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      await getInsurer();
      await getEnums();
    })();
    setIsLoading(false);
    // eslint-disable-next-line
  }, []);
  return [
    {
      name: ['complaint_url'],
      tooltip: true,
      content: insurer && insurer.complaint_url
    },
    {
      name: ['complaint_capital'],
      rules: [{ required: true }],
      input: <InputNumber />,
      content: insurer && insurer.capital
    },
    {
      name: ['complaint_email'],
      rules: [
        {
          required: true,
          type: 'email',
          message: t('errors.form.EMAIL')
        }
      ],
      tooltip: true,
      content: insurer && insurer.complaint_email
    },
    {
      name: ['complaint_responsible'],
      rules: [{ required: true }, ...useXmlPattern()],
      tooltip: true,
      content: insurer && insurer.complaint_responsible
    },
    {
      name: ['complaint_company_name'],
      rules: [{ required: true }, ...useXmlPattern()],
      tooltip: true,
      content: insurer && insurer.complaint_company_name
    },
    {
      name: ['complaint_street_number'],
      rules: [{ required: true }],
      input: <InputNumber />,
      content: insurer && insurer.complaint_street_number,
      span: 3
    },
    {
      name: ['complaint_street'],
      rules: [{ required: true }],
      content: insurer && insurer.complaint_street
    },
    {
      name: ['complaint_additional'],
      content: insurer && insurer.complaint_additional
    },
    {
      name: ['complaint_postal_code'],
      rules: [{ required: true }],
      content: insurer && insurer.complaint_postal_code,
      input: (
        <Input
          style={{ width: '90px' }}
          onChange={handlePostalCodeChange}
          autoComplete="off"
        />
      ),
      hasFeedback: isLoading,
      layout: isLoading && { validateStatus: 'validating' }
    },
    {
      name: ['complaint_city'],
      rules: [{ required: true }],
      content: insurer && insurer.complaint_city,
      input: (
        <Select
          showSearch
          onSelect={(selected) => {
            form.setFieldsValue({
              complaint_postal_code: cities.find(
                (city) => city.nom === selected
              ).code
            });
          }}
          options={
            cities &&
            cities
              .filter((city) => city.codePostal.startsWith(postalCode))
              .map((city) => ({
                label: `${city.nom} (${city.codePostal})`,
                value: city.nom
              }))
          }
        />
      ),
      hasFeedback: isLoading,
      layout: isLoading && { validateStatus: 'validating' }
    },
    {
      name: ['complaint_responsible_email'],
      rules: [
        {
          required: true,
          type: 'email',
          message: t('errors.form.EMAIL')
        }
      ],
      tooltip: true,
      content: insurer && insurer.complaint_responsible_email
    },
    {
      name: ['complaint_url_responsible'],
      tooltip: true,
      content: insurer && insurer.complaint_url_responsible
    },
    {
      name: ['category_insurer'],
      input: insurer && (
        <Select>
          {enums.categoryInsurer &&
            enums?.categoryInsurer.map((e) => (
              <Option value={e} key={e}>
                {t(`insurers.category.${e}`)}
              </Option>
            ))}
        </Select>
      ),
      tooltip: true
    }
  ];
};

export default useFields;
